import {
    Container,
    Grid,
    Chip,
    Box,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { TagViewModel } from '../../../../types/tags.types';
import Button from '../../foundation/Button';
import { ColCenteredFlex } from '../../foundation/Flex';
import { fixNumberCommas } from '../../utils/math.utils';
import { GeoAutocomplete, LocationOption, TagsSearchAutocomplete } from '../AutocompleteRemote';
import { MdUpBox } from '../../foundation/MediaQueryBox';

export type SearchJobsProps = {
    totalJobs?: number
    numberOfTodayJobs?: number
    defaultLocation?: any,
}

const pulse = keyframes`
    0%, 100% {
        box-shadow: 0 4px 20px rgba(0, 132, 63, 0.35), 0 0 0 0 rgba(0, 132, 63, 0.4);
    }
    50% {
        box-shadow: 0 6px 30px rgba(0, 132, 63, 0.45), 0 0 0 8px rgba(0, 132, 63, 0);
    }
`;

const float = keyframes`
    0%, 100% {
        transform: translateY(0px);
    }
    50% {
        transform: translateY(-8px);
    }
`;

const AIBadgeStyled = styled(Box)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    background: linear-gradient(135deg, #00843f 0%, #006633 100%);
    padding: 0.7rem 2.2rem;
    border-radius: 0.5rem;
    margin-top: 2rem;
    margin-bottom: 1rem;
    animation: ${pulse} 2.5s ease-in-out infinite;
    position: relative;
    overflow: hidden;
    

    & > span:first-of-type {
        font-size: 1.4rem;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        animation: ${float} 3s ease-in-out infinite;
        z-index: 1;
    }

    & > span:last-of-type {
        color: white;
        font-weight: 700;
        font-size: 1.05rem;
        letter-spacing: 0.5px;
        text-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        z-index: 1;
    }
`;

const NumberWrapper = styled('span')<{ showBG: boolean }>`
    ${({ showBG }) => (showBG ? 'background: linear-gradient(135deg, #00843f 0%, #006633 100%)' : '')};
    color: ${({ showBG }) => (showBG ? '#ffffff' : '#00843f')};
    font-size: 3rem;
    padding: ${({ showBG }) => (showBG ? '0.3rem 0.7rem' : '0 0.1rem')};
    margin: 0 0.15rem;
    font-weight: 800;
    border-radius: 0.6rem;
    box-shadow: ${({ showBG }) => (showBG ? '0 6px 20px rgba(0, 132, 63, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)' : 'none')};
    transition: transform 0.3s ease;
    
    &:hover {
        transform: ${({ showBG }) => (showBG ? 'scale(1.05)' : 'none')};
    }
    
    ${props => props.theme.breakpoints.down('sm')} {
        font-size: 2.5rem;
        padding: ${({ showBG }) => (showBG ? '0.2rem 0.5rem' : '0 0.1rem')};
    }
`;

const AIFeaturesStyled = styled(Box)`
    margin-top: 2.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 1.2rem;
    justify-content: center;
    padding: 0 1rem;
    
    & .MuiChip-root {
        background: linear-gradient(135deg, rgba(0, 132, 63, 0.08) 0%, rgba(0, 132, 63, 0.12) 100%);
        color: #00843f;
        border: 2px solid transparent;
        background-clip: padding-box;
        position: relative;
        font-weight: 600;
        font-size: 1rem;
        padding: 0.5rem;
        height: auto;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 8px rgba(0, 132, 63, 0.1);
        
        &:before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: inherit;
            padding: 2px;
            background: linear-gradient(135deg, rgba(0, 132, 63, 0.3), rgba(0, 132, 63, 0.15));
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
        }
        
        &:hover {
            background: linear-gradient(135deg, rgba(0, 132, 63, 0.15) 0%, rgba(0, 132, 63, 0.2) 100%);
            transform: translateY(-4px) scale(1.02);
            box-shadow: 0 8px 20px rgba(0, 132, 63, 0.3);
            
            &:before {
                background: linear-gradient(135deg, rgba(0, 132, 63, 0.6), rgba(0, 132, 63, 0.3));
            }
        }
    }
    
    ${props => props.theme.breakpoints.down('sm')} {
        gap: 0.8rem;
        margin-top: 1.5rem;
        
        & .MuiChip-root {
            font-size: 0.9rem;
            padding: 0.5rem 1rem;
        }
    }
`;

const SearchJobsFormStyled = styled(Box)`
    max-width: 52rem;
    margin-top: 3rem;
    margin-bottom: 2rem;
    margin-left: auto;
    margin-right: auto;
    padding: 3rem;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    box-shadow: 0 20px 60px rgba(0, 132, 63, 0.15), 0 8px 20px rgba(0, 0, 0, 0.08);
    border: 2px solid transparent;
    border-radius: 0.5rem;
    background-clip: padding-box;
    position: relative;
    transition: all 0.4s ease;
    
    &:after {
        content: '';
        position: absolute;
        inset: -100px;
        background: radial-gradient(circle at center, rgba(0, 132, 63, 0.05) 0%, transparent 70%);
        z-index: -2;
        pointer-events: none;
    }
    
    &:hover {
        box-shadow: 0 25px 70px rgba(0, 132, 63, 0.2), 0 10px 25px rgba(0, 0, 0, 0.1);
        /* transform: translateY(-2px); */
        
        &:before {
            opacity: 0.6;
        }
    }
    
    ${props => props.theme.breakpoints.down('md')} {
        max-width: 90%;
        padding: 2.5rem;
    }
    
    ${props => props.theme.breakpoints.down('sm')} {
        padding: 2rem;
        margin-top: 2rem;
        border-radius: 1.2rem;
    }
`;

const SearchHintStyled = styled(Box)`
    text-align: center;
    color: #00843f;
    font-size: 1rem;
    margin-top: 1.2rem;
    font-weight: 500;
    line-height: 1.6;
    padding: 0.8rem 1.5rem;
    background: linear-gradient(135deg, rgba(0, 132, 63, 0.03) 0%, rgba(0, 132, 63, 0.08) 100%);
    border-radius: 1rem;
    border: 1px solid rgba(0, 132, 63, 0.15);
    
    & > span:first-of-type {
        display: inline-block;
        margin-left: 0.4rem;
        font-size: 1.2rem;
        animation: ${float} 2.5s ease-in-out infinite;
    }
    
    ${props => props.theme.breakpoints.down('sm')} {
        font-size: 0.9rem;
        padding: 0.6rem 1rem;
    }
`;

const StyledSearchButton = styled(Button)`
    margin-top: 2rem;
    background: linear-gradient(135deg, #00843f 0%, #006633 100%) !important;
    box-shadow: 0 6px 24px rgba(0, 132, 63, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
    text-transform: none !important;
    font-size: 1.3rem !important;
    font-weight: 700 !important;
    padding: 1.1rem 2.5rem !important;
    position: relative;
    overflow: hidden;
    
    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
        );
        transition: left 0.5s ease;
    }
    
    &:hover {
        background: linear-gradient(135deg, #007a4d 0%, #005529 100%) !important;
        box-shadow: 0 10px 35px rgba(0, 132, 63, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
        
        &:before {
            left: 100%;
        }
    }
    
    &:active {
        transform: translateY(-2px) scale(1.01);
    }
    
    ${props => props.theme.breakpoints.down('sm')} {
        font-size: 1.15rem !important;
        padding: 1rem 2rem !important;
    }
`;

const SearchJobs = (props: SearchJobsProps): JSX.Element => {
    const [search, setSearch] = useState<TagViewModel | string>(null);
    const [location, setLocation] = useState<LocationOption>(null);

    const locationChange = (_ev, val) => { setLocation(val); };
    const searchChange = useCallback((_ev, val: TagViewModel | string) => {
        setSearch(val);
    }, []);

    const onSearchClick = () => {
        window.showBackdrop();
        let url = '';

        if (search) {
            url = typeof search === 'string' ? `/job/search/${encodeURIComponent(search.replace(/ /g, '-'))}`
                : `/job/tagged/${encodeURIComponent(search.display)}`;
        } else {
            url = '/job/search';
        }

        if (location) {
            if (location.areaid || location.cityid) url += `?loc=${location.formatted_address.replace(/ /g, '-')}`;
            else url += `?lat=${location.latitude}&lng=${location.longitude}&address=${encodeURIComponent(location.formatted_address)}`;
        }
        window.location.assign(url || '/job/search');
    };

    return <>
        {/* <Container maxWidth="lg">
            <ColCenteredFlex>
                <AIBadgeStyled>
                    <span>✨</span>
                    <span>חיפוש מתקדם מבוסס בינה מלאכותית</span>
                </AIBadgeStyled>
                <Box sx={{ textAlign: 'center', marginTop: '1rem' }}>
                    {fixNumberCommas(props.totalJobs?.toString() || '    ').split('').map(
                        (c, i) => <NumberWrapper key={i} showBG={c !== ','}>{c}</NumberWrapper>,
                    )}
                </Box>
                <Box sx={{
                    textAlign: 'center',
                    marginTop: '0.5rem',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    color: '#00843f',
                }}>
                    משרות פעילות באתר
                </Box>
                <MdUpBox>
                    <AIFeaturesStyled>
                        <Chip label="🎯 התאמה אינטליגנטית למשרות" />
                        <Chip label="🚀 תוצאות חיפוש מיידיות" />
                        <Chip label="💡 הבנה חכמה של כוונת החיפוש" />
                        <Chip label={`⚡ ${props.numberOfTodayJobs || 0} משרות חדשות היום`} />
                    </AIFeaturesStyled>
                </MdUpBox>
            </ColCenteredFlex>
        </Container> */}
        <SearchJobsFormStyled>
            <Grid container spacing={2.5}>
                <Grid item sm={8} xs={12}>
                    <TagsSearchAutocomplete
                        onEnter={onSearchClick}
                        onInputChange={searchChange}
                        size="medium" onChange={searchChange} />
                </Grid>
                <Grid item sm={4} xs={12}>
                    <GeoAutocomplete
                        defaultValue={props.defaultLocation}
                        size="medium"
                        onChange={locationChange} />
                </Grid>
            </Grid>
            <StyledSearchButton
                onClick={onSearchClick}
                fullWidth
                size="large"
                variant="contained"
            >
                🔍 חיפוש חכם עם AI
            </StyledSearchButton>
            <SearchHintStyled>
                <span>💬</span>
                &nbsp;&nbsp;תארו את המשרה המבוקשת בשפה חופשית - הבינה המלאכותית שלנו תבין אתכם
            </SearchHintStyled>
        </SearchJobsFormStyled>
    </>;
};

export default React.memo(SearchJobs);
